// @flow

import { Map, Record, type RecordOf } from 'immutable';
import PropTypes from 'prop-types';
import * as React from 'react';
import UPParser from 'ua-parser-js';

type Facing = 'left' | 'right';
type CustomerProps = {
  kind: string,
  subKind: number,
  facing: Facing,
  animationDelay: number,
  animationDuration: number,
  offsetY: number,
};
type CustomerType = RecordOf<CustomerProps>;

const DESIGN_TOP = 278;
const DESIGN_WIDTH = 1140;
const DESIGN_HEIGHT = 410;

const DESIGN_CUSTOMER_WIDTH = 100;
const DESIGN_CUSTOMER_HEIGHT = 160;

const makeCustomer = Record({
  kind: 0,
  subKind: 1,
  facing: 'left',
  animationDelay: 0,
  animationDuration: 0,
  offsetY: 0,
});

const genCustomer = (kind: string): CustomerType => {
  const facing = Math.random() > 0.5 ? 'left' : 'right';
  const animationDelay = Math.random() * 3.5;
  const animationDuration = 4.5 + Math.random() * 3.5;
  const offsetY = Math.random();
  const subKind = Math.floor(Math.random() * 6) + 1;

  return makeCustomer({ facing, animationDelay, animationDuration, offsetY, subKind, kind });
};

const isIOS10 = () => {
  let parser = new UPParser();
  if (parser.getOS().name === 'iOS') {
    const regex = /(\d+)/;
    let major = parser.getOS().version.match(regex);
    if (!major) return false;
    if (major[1] && Number(major[1]) == 10) {
      return true;
    }
  }
  return false;
};

const Customer = (props: { scale: number, customer: CustomerType, onAnimationIteration: (*) => void }): React.Node => {
  let style = {
    bottom: `${props.scale * props.customer.offsetY * (DESIGN_HEIGHT - DESIGN_CUSTOMER_HEIGHT)}px`,
    height: `${props.scale * DESIGN_CUSTOMER_HEIGHT}px`,
    width: `${props.scale * DESIGN_CUSTOMER_WIDTH}px`,
    animationDelay: `${props.customer.animationDelay}s`,
    animationDuration: `${props.customer.animationDuration}s`,
    zIndex: `${1000 - Math.floor(props.customer.offsetY * 1000)}`,
  };
  let classNames = ['customer-wrapper'];
  if (props.customer.facing === 'right') {
    classNames.push('customer-flip');
  }
  const className = classNames.join(' ');

  let customerKind = '';
  switch (props.customer.kind) {
    case '0':
      customerKind = 'male';
      break;
    case '1':
      customerKind = 'female';
      break;
    case '2':
      customerKind = 'kid';
      break;
    default:
      customerKind = 'unknown';
      break;
  }

  /*
   * Hack:
   * For iOS10, the animation is too heavy to control the price bar.
   * So, disable animation.
   */
  return (
    <div className={className} style={style} onAnimationIteration={props.onAnimationIteration}>
      <div className={`customer customer-${customerKind}-0${props.customer.subKind} ${isIOS10() ? 'stop' : ''}`} />
    </div>
  );
};

type Props = {
  customerCounts: Map<string, number>,
  scale: number,
};

export default class CustomerSimulation extends React.PureComponent<Props> {
  static propTypes = {
    customerCounts: PropTypes.object.isRequired,
  };

  _customers: {
    [string]: CustomerType[],
  } = {};

  customers() {
    let results: CustomerType[] = [];

    const { customerCounts } = this.props;

    for (const customerKind of ['0', '1', '2']) {
      this._customers[customerKind] = this._customers[customerKind] || [];
      const count = customerCounts.get(customerKind, 0);

      for (let i = this._customers[customerKind].length; i < count; i++) {
        this._customers[customerKind].push(genCustomer(customerKind));
      }

      results = results.concat(this._customers[customerKind]);
    }

    /*
     * Hack:
     * For iOS10, the animation is too heavy to control the price bar.
     * So, Make them one third.
     */
    if (isIOS10()) {
      results = results.filter((_element, index) => {
        return index % 3 === 0;
      });
    }

    return results;
  }

  onAnimationIteration(customer: CustomerType, e: *) {
    if (e.animationName === 'sprite-animation-15') return;

    if (this.props.customerCounts.get(customer.kind, 0) < this._customers[customer.kind].length) {
      const index = this._customers[customer.kind].indexOf(customer);
      if (index > -1) {
        this._customers[customer.kind].splice(index, 1);
      }
    }
    this.forceUpdate();
  }

  render() {
    const style = {
      top: `${this.props.scale * DESIGN_TOP}px`,
      height: `${this.props.scale * DESIGN_HEIGHT}px`,
      width: `${this.props.scale * DESIGN_WIDTH}px`,
    };
    return (
      <div className="customer-simulation" style={style}>
        {this.customers().map(customer => (
          <Customer
            key={customer.hashCode()}
            scale={this.props.scale}
            customer={customer}
            onAnimationIteration={e => this.onAnimationIteration(customer, e)}
          />
        ))}
      </div>
    );
  }
}
